import { PricingContextProvider } from '@vp/react-pricing-context';
import React, { useEffect, useState } from 'react';
import { Box, SiteContent, SiteMain } from '@vp/swan';
import { useLogger, useSwanStyleKeys, useUserContext } from '@vp/ubik-context';
import { FragmentLogger } from '@vp/ubik-logging';
import i18n, { Resource } from 'i18next';
import {
    AUTH_INTERNAL_CLIENT_PARAM,
    AuthDevMode,
    Breadcrumbs,
    ClientOverride,
    IdentityProvider,
} from '@99designs/design-services-common';
import {
    FeatureFlagProvider,
    VistaExperiment,
    VistaExperimentsProvider,
    featureFlagsFromUbikUserContext,
    vistaExperimentsFromUbikUserContext,
} from '@99designs/experiments';
import {
    ProductOptionsQuery,
    ServiceDetailsPageDataQuery,
} from '@99designs/feature-service-details';
import { AuthenticatedApolloProvider } from '@99designs/graph-utils';
import { getCountryCode } from '@99designs/i18n';
import { PAGE_TRACKING_CONFIG, PageProvider } from '@99designs/tracking';
import { Content } from '../components/content';
import { LOGGER_FRAGMENT_ID } from '../lib/consts';
import {
    EnvironmentConfig,
    EnvironmentConfigProvider,
} from '../lib/environment/environmentContext';

export interface Props {
    localeReference: string;
    resources: Resource;
    locale: string;
    serviceDetails: ServiceDetailsPageDataQuery['serviceDetailPage'];
    price: ServiceDetailsPageDataQuery['expertServices'][0]['price'];
    routeSpecifiedMpvId: string;
    serviceMpvId: string;
    printMpvId?: string;
    workId: string;
    isProductDesignLiveEnabled: boolean;
    serviceName: string;
    envConfig: EnvironmentConfig['public'];
    servicePrd: string;
    serviceVersion: number;
    expertServices: ServiceDetailsPageDataQuery['expertServices'];
    productOptions: ProductOptionsQuery['productOptions'];
}

const authDevMode = (): AuthDevMode => {
    const isAuthDevMode = process.env.ENVIRONMENT === 'dev';
    if (!isAuthDevMode) {
        return false;
    }

    const clientID = process.env.AUTH0_CLIENT;
    if (!clientID) {
        return true;
    }

    return { clientID, domain: 'oauth.cimpress.io' };
};

const INTERNAL_ACCESS_CLIENT: ClientOverride = {
    options: {
        connection: 'vistaprintcom-waad',
    },
    developmentMode: {
        clientID: 'dFzz8qvA9epUe28InBJYrBE4U8RuuVzQ', // This is not a secret, you can find it in the access client URL above.
        // Only set redirectUriOrigin on vista based domains. /auth-callback/ doesn't register when redirecting on localhost
        ...(process.env.NODE_ENV !== 'development' && {
            redirectUriOrigin:
                typeof window !== 'undefined'
                    ? `${window.location.origin}/auth-callback/`
                    : undefined,
        }),
    },
};

function buildAuthConfiguration(): ClientOverride {
    if (typeof window !== 'undefined') {
        const shouldUseInternalAuthClient = new URLSearchParams(window.location.search).get(
            AUTH_INTERNAL_CLIENT_PARAM
        );

        if (shouldUseInternalAuthClient) {
            return INTERNAL_ACCESS_CLIENT;
        }
    }

    return {
        developmentMode: authDevMode(),
    };
}

export const App = ({
    resources,
    serviceDetails,
    expertServices,
    price,
    routeSpecifiedMpvId,
    serviceMpvId,
    printMpvId,
    workId,
    isProductDesignLiveEnabled,
    serviceName,
    envConfig,
    servicePrd,
    serviceVersion,
    productOptions,
}: Props) => {
    const logger = useLogger() as FragmentLogger;
    const userContext = useUserContext();
    logger.setContext({ fragmentId: LOGGER_FRAGMENT_ID });

    const [authConfig, setAuthConfig] = useState<ClientOverride>({
        developmentMode: authDevMode(),
    });

    useEffect(() => {
        setAuthConfig(buildAuthConfiguration());
    }, []);

    i18n.init({
        resources,
        debug: envConfig.enableTranslationDebug,
        lowerCaseLng: true, //locales are set to lower case e.g. "en-us" not "en-US"
        interpolation: {
            escapeValue: false, // React already provides escaping
        },
        nsSeparator: '*****', // namespace selector is a string delimited which defaults to ":". Overriding to *** so that urls don't get split
        returnObjects: true,
    });
    i18n.changeLanguage(userContext.locale);

    useSwanStyleKeys([
        'anchorBar',
        'accordion',
        'alertBox',
        'avatar',
        'banner',
        'button',
        'carousel',
        'core',
        'grid',
        'hidden',
        'icon',
        'listbox',
        'modalDialog',
        'tabs',
        'thumbnailsHero',
        'pagination',
        'popover',
        'progressiveImage',
        'selectionSet',
        'spinner',
        'standardTile',
        'toggleSwitch',
        'utility',
        'visible',
        'zoomControls',
        'fileDropzone',
        'fileTile',
        'table',
    ]);

    const experiments: VistaExperiment[] = userContext?.experiments
        ? vistaExperimentsFromUbikUserContext(userContext?.experiments, logger)
        : [];

    const featureFlags = featureFlagsFromUbikUserContext(userContext?.featureFlags);
    const breadcrumbs = [
        {
            url: '',
            label: serviceName,
        },
    ];

    const pageTracking = {
        ...PAGE_TRACKING_CONFIG['product-page'],
        pagePath: `/experts/services/details/${routeSpecifiedMpvId.toLowerCase()}`,
    };

    return (
        <EnvironmentConfigProvider value={envConfig}>
            <VistaExperimentsProvider value={experiments}>
                <FeatureFlagProvider value={featureFlags}>
                    <IdentityProvider locale={userContext.locale} {...authConfig}>
                        <PricingContextProvider
                            initializationConfig={{
                                merchantId: 'vistaprint',
                                market: getCountryCode(userContext.locale),
                                isDevelopmentMode: process.env.NODE_ENV === 'development',
                            }}
                        >
                            <PageProvider
                                locale={userContext.locale}
                                pageData={{
                                    ...pageTracking,
                                    pageName: `${routeSpecifiedMpvId}-design:${pageTracking.pageSection}`,
                                }}
                            >
                                <AuthenticatedApolloProvider
                                    locale={userContext.locale}
                                    endpoint={envConfig.graphqlHost}
                                    hideLoader={true}
                                >
                                    <Breadcrumbs crumbs={breadcrumbs} />
                                    <Box as="main" id="maincontentstart">
                                        <SiteContent>
                                            <SiteMain>
                                                <Content
                                                    environment={userContext.environment}
                                                    serviceDetails={serviceDetails}
                                                    price={price}
                                                    routeSpecifiedMpvId={routeSpecifiedMpvId}
                                                    serviceMpvId={serviceMpvId}
                                                    printMpvId={printMpvId}
                                                    workId={workId}
                                                    isProductDesignLiveEnabled={
                                                        isProductDesignLiveEnabled
                                                    }
                                                    productName={serviceName}
                                                    servicePrd={servicePrd}
                                                    serviceVersion={serviceVersion}
                                                    expertServices={expertServices}
                                                    productOptions={productOptions}
                                                />
                                            </SiteMain>
                                        </SiteContent>
                                    </Box>
                                </AuthenticatedApolloProvider>
                            </PageProvider>
                        </PricingContextProvider>
                    </IdentityProvider>
                </FeatureFlagProvider>
            </VistaExperimentsProvider>
        </EnvironmentConfigProvider>
    );
};
